import React from 'react';

/**
 * BreakdownTable — Reusable sorted table for event/category/segment breakdowns.
 *
 * Props:
 *   data: Array<{ [labelKey]: string, [valueKey]: number }>
 *   labelKey: string (column name for labels)
 *   valueKey: string (column name for values)
 *   labelHeader: string
 *   valueHeader: string
 *   onRowClick: (row) => void
 *   maxRows: number
 *   emptyMessage: string
 */

const BreakdownTable = ({
    data = [], labelKey = 'event_name', valueKey = 'count',
    labelHeader = 'Name', valueHeader = 'Count',
    onRowClick = null, maxRows = 20,
    emptyMessage = 'No data available',
}) => {
    const rows = data.slice(0, maxRows);
    const numericValues = rows.map(r => typeof r[valueKey] === 'number' ? r[valueKey] : 0);
    const maxValue = Math.max(...numericValues, 1);
    const hasNumericValues = numericValues.some(v => v > 0);

    if (rows.length === 0) {
        return (
            <div style={{
                textAlign: 'center', padding: '24px 0',
                color: '#94a3b8', fontSize: 13,
            }}>
                {emptyMessage}
            </div>
        );
    }

    return (
        <div style={{
            background: '#fff', border: '1px solid #e5e7eb',
            borderRadius: 12, overflow: 'hidden',
        }}>
            {/* Header */}
            <div style={{
                display: 'grid', gridTemplateColumns: '1fr auto',
                padding: '10px 16px', background: '#f8fafc',
                borderBottom: '1px solid #e5e7eb',
            }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                    {labelHeader}
                </span>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', textAlign: 'right' }}>
                    {valueHeader}
                </span>
            </div>

            {/* Rows */}
            {rows.map((row, i) => {
                const numVal = typeof row[valueKey] === 'number' ? row[valueKey] : 0;
                return (
                <div
                    key={i}
                    onClick={() => onRowClick && onRowClick(row)}
                    style={{
                        display: 'grid', gridTemplateColumns: '1fr auto',
                        padding: '10px 16px',
                        borderBottom: i < rows.length - 1 ? '1px solid #f1f5f9' : 'none',
                        cursor: onRowClick ? 'pointer' : 'default',
                        transition: 'background 0.1s',
                        position: 'relative',
                    }}
                    onMouseOver={e => { if (onRowClick) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseOut={e => { if (onRowClick) e.currentTarget.style.background = 'transparent'; }}
                >
                    {/* Bar indicator — only for numeric values */}
                    {hasNumericValues && (
                    <div style={{
                        position: 'absolute', left: 0, top: 0, bottom: 0,
                        width: `${(numVal / maxValue) * 100}%`,
                        background: '#6366f108',
                        borderRadius: '0 4px 4px 0',
                    }} />
                    )}

                    <span style={{ fontSize: 13, color: '#374151', fontWeight: 500, position: 'relative' }}>
                        {row[labelKey]}
                    </span>
                    <span style={{
                        fontSize: 13, color: '#111827', fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums', position: 'relative',
                    }}>
                        {typeof row[valueKey] === 'number' ? row[valueKey].toLocaleString() : row[valueKey]}
                    </span>
                </div>
                );
            })}
        </div>
    );
};

export default BreakdownTable;
